'use client';

import { useMediaQuery } from '@uidotdev/usehooks';
import { getCssVariable } from '@bridebook/aura';
import { useMemo } from 'react';
import { BREAKPOINTS } from '../constants/breakpoints';
import type { Breakpoint } from '../types/breakpoints';

/**
 * Hook to check if the current viewport matches or exceeds a specific breakpoint. (Mobile first approach)
 * Uses Tailwind CSS custom breakpoint variables defined in breakpoints.css.
 *
 * @param breakpoint - The breakpoint CSS variable to check against
 * @returns boolean - True if the viewport width is greater than or equal to the breakpoint
 *
 * @example
 * ```tsx
 * const isDesktop = useIsBreakpoint('--breakpoint-xl'); // >= 1260px
 * const isTablet = useIsBreakpoint('--breakpoint-md'); // >= 768px
 * ```
 */
export const useIsBreakpoint = (breakpoint: Breakpoint) => {
  const breakpointValue = useMemo(() => {
    try {
      return getCssVariable(breakpoint);
    } catch {
      // For example when CSS variable not available yet (race condition during hydration)
      return BREAKPOINTS[breakpoint];
    }
  }, [breakpoint]);
  const isBreakpoint = useMediaQuery(`(min-width: ${breakpointValue})`);

  return isBreakpoint;
};
